import { Button } from '../../components/Button';
import { OnScreenKeyboard } from '../../components/OnScreenKeyboard';
import type { BoardProps, InputKey } from '../../engine/types';
import { cx } from '../../utils/classnames';
import { requiredLetter, type BranchesMove, type BranchesState } from './logic';

export function BranchesBoard({ state, status, dispatch, feedback }: BoardProps<BranchesState, BranchesMove>) {
  const playing = status === 'playing';
  const req = requiredLetter(state);
  const lastError = feedback?.tone === 'error' ? feedback.nonce : 0;
  const startsOk = state.current.length === 0 || state.current[0] === req;

  const onKey = (key: InputKey) => {
    if (key.type === 'char') dispatch({ type: 'add', ch: key.value });
    else if (key.type === 'backspace') dispatch({ type: 'remove' });
    else dispatch({ type: 'submit' });
  };

  return (
    <div className="cv-board">
      <div className="cv-branches">
        <div className="cv-branches__progress">
          <span className="cv-mono">
            {state.chain.length}/{state.target}
          </span>
          {playing ? (
            <span>
              {' '}
              · next starts with <strong className="cv-branches__req">{req.toUpperCase()}</strong>
            </span>
          ) : null}
        </div>

        <div className="cv-branches__chain" role="list">
          {state.chain.map((w, i) => (
            <span className="cv-branches__word" role="listitem" key={i} data-seed={i === 0 ? 'true' : undefined}>
              {w.toUpperCase()}
            </span>
          ))}
        </div>

        {playing ? (
          <div
            key={`entry-${lastError}`}
            className={cx('cv-branches__entry', lastError && 'cv-anim-shake')}
            aria-live="polite"
          >
            {state.current.length === 0 ? (
              <span className="cv-faint">word starting with {req.toUpperCase()}</span>
            ) : (
              state.current.split('').map((ch, i) => (
                <em key={i} data-bad={i === 0 && !startsOk ? 'true' : undefined}>
                  {ch.toUpperCase()}
                </em>
              ))
            )}
            <span className="caret" aria-hidden="true" />
          </div>
        ) : null}

        {playing && state.chain.length >= 2 ? (
          <Button variant="ghost" iconLeft="check" onClick={() => dispatch({ type: 'finish' })}>
            Bank chain of {state.chain.length}
          </Button>
        ) : null}
      </div>

      <div className="cv-board__status" data-tone={feedback?.tone === 'error' ? 'error' : undefined} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>

      <OnScreenKeyboard onKey={onKey} disabled={!playing} />
    </div>
  );
}
